'use client';

import { ChangeEventHandler, FC, KeyboardEvent, MouseEvent, Ref, useMemo, useRef, useState } from 'react';

import { useAriaErrorMessageId } from '../../../../hooks/useAriaErrorMessageId/useAriaErrorMessageId';
import { useForwardedRef } from '../../../../hooks/useForwardedRef/useForwardedRef';
import useId from '../../../../hooks/useId/useId';
import { Icon } from '../../../icon/components/Icon/Icon';
import { List } from '../../../list/components/List/List';
import { ListItem } from '../../../list/components/ListItem/ListItem';
import { Menu } from '../../../menu/components/Menu/Menu';
import { HighlightText } from '../../../typography/components/HighlightText/HighlightText';
import { Typography } from '../../../typography/components/Typography/Typography';

export interface AutocompleteOption {
   id: string;
   value: string;
}

interface Props {
   inputRef?: Ref<HTMLInputElement>;
   id?: string;
   name?: string;
   clearable?: boolean;
   disabled?: boolean;
   error?: boolean;
   errorMessage?: string;
   label?: string;
   options: Array<AutocompleteOption>;
   readOnly?: boolean;
   required?: boolean;
   supportText?: string;
   noResultsText?: string;
   noResultsSupportText?: string;
   iconType?: 'flag' | 'default';
   leadingIcon?: string;
   trailingIcon?: string;
   value?: AutocompleteOption;
   width?: string | number;
   onClear?: () => void;
   onChange?: (value: AutocompleteOption | undefined) => void;
   onSearch?: (value: string) => void;
   onFocus?: () => void;
   onBlur?: () => void;
}

// Mouse enter is funky
export const Autocomplete: FC<Props> = ({
   inputRef: refFromProps,
   id: idFromProps,
   name,
   clearable = false,
   disabled = false,
   error = false,
   errorMessage,
   label,
   options,
   readOnly = false,
   required = false,
   supportText,
   noResultsText,
   noResultsSupportText,
   iconType = 'default',
   leadingIcon,
   trailingIcon,
   value,
   width,
   onClear,
   onChange,
   onSearch,
   onFocus,
   onBlur
}) => {
   const id = useId(idFromProps, 'autocomplete');
   const menuId = `${id}-menu`;
   const errorMessageId = useAriaErrorMessageId(error, 'autocomplete');

   const [searchValue, setSearchValue] = useState('');
   const [menuOpen, setMenuOpen] = useState(false);
   const [focused, setFocused] = useState(false);
   const [focusedIndex, setFocusedIndex] = useState(-1);

   const inputRef = useForwardedRef(refFromProps);
   const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

   const visualOptions = useMemo(() => {
      if (searchValue) return options.filter((o) => o.value.toLowerCase().includes(searchValue.toLowerCase()));
      return options;
   }, [searchValue, options]);

   const getOptionId = (option: AutocompleteOption) => `${menuId}-${option.id}`;

   const activeOptionId = !visualOptions[focusedIndex] ? undefined : getOptionId(visualOptions[focusedIndex]);
   const inputValue = searchValue || value?.value || '';

   const openMenu = () => {
      if (value) {
         const focussedIndex = visualOptions.findIndex((option) => option.id === value.id);
         setFocusedIndex(focussedIndex);
      }
      setMenuOpen(true);
   };

   const closeMenu = () => {
      setFocusedIndex(-1);
      setMenuOpen(false);
   };

   const toggleMenu = () => {
      if (menuOpen) {
         closeMenu();
      } else {
         openMenu();
      }
   };

   const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === inputRef.current) {
         if (inputValue === '') {
            toggleMenu();
         } else if (!menuOpen) {
            openMenu();
         }
      } else {
         event.preventDefault();
         inputRef.current?.focus();

         toggleMenu();
      }
   };

   const handleFocus = () => {
      setFocused(true);
      onFocus?.();
   };

   const handleBlur = () => {
      setFocused(false);
      onBlur?.();
      setSearchValue('');
      setFocused(false);
      closeMenu();
   };

   const handleChange = (option: AutocompleteOption) => {
      if (value && option.id === value.id) return;
      onChange?.(option);
      setSearchValue('');
      closeMenu();
   };

   const handleOptionClick = (event: MouseEvent<HTMLElement>, option: AutocompleteOption) => {
      event.preventDefault();
      handleChange(option);
   };

   const handleClear = () => {
      onChange?.(undefined);
      onClear?.();
   };

   const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      if (event.target.value === '' && value) {
         onChange?.(undefined);
      }
      onSearch?.(event.target.value);
      setSearchValue(event.target.value);
      setFocusedIndex(-1);
      openMenu();
   };

   const handleFocusedIndexChange = (nextFocusedIndex: number) => {
      if (!menuOpen) {
         openMenu();
         return;
      }

      let calculatedNextFocusedIndex = nextFocusedIndex;
      if (calculatedNextFocusedIndex < 0) calculatedNextFocusedIndex = 0;
      else if (calculatedNextFocusedIndex > visualOptions.length - 1) calculatedNextFocusedIndex = visualOptions.length - 1;
      setFocusedIndex(calculatedNextFocusedIndex);
      optionRefs.current[calculatedNextFocusedIndex]?.scrollIntoView({
         block: 'nearest'
      });
   };

   const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Space' && !inputValue) {
         event.stopPropagation();
         event.preventDefault();
         openMenu();
      } else if (event.key === 'ArrowUp') {
         event.preventDefault();
         const nextFocusedIndex = focusedIndex - 1;
         handleFocusedIndexChange(nextFocusedIndex);
      } else if (event.key === 'ArrowDown') {
         event.preventDefault();
         const nextFocusedIndex = focusedIndex + 1;
         handleFocusedIndexChange(nextFocusedIndex);
      } else if (event.key === 'Enter' && !!visualOptions[focusedIndex]) {
         handleChange(visualOptions[focusedIndex]);
      }
   };

   return (
      <div
         data-testid={`autocomplete${error ? '--error' : ''}${readOnly ? '--read-only' : ''}${menuOpen ? '--open' : ''}`}
         style={{ width }}
         className={`gap-component-input-combobox-container-spacing-outer-gap max-w-component-data-display-text-container-body-sizing-max-width-desktop flex w-full flex-col ${disabled || readOnly ? 'pointer-events-none' : ''}`}
      >
         <Menu
            content={
               <div id={menuId} data-testid="autocomplete-menu" className="overflow-y-auto">
                  <List role="listbox">
                     {!visualOptions ||
                        (visualOptions?.length === 0 && (
                           <ListItem disabled>
                              <Typography data-testid="autocomplete-noResultsText" whiteSpace="nowrap" variant="small" color="subtle">
                                 {noResultsText}
                              </Typography>
                              <Typography data-testid="autocomplete-noResultsSupportText" whiteSpace="nowrap" variant="small" color="subtle">
                                 {noResultsSupportText}
                              </Typography>
                           </ListItem>
                        ))}

                     {visualOptions.map((option, index) => (
                        <ListItem
                           ref={(el) => {
                              optionRefs.current[index] = el;
                           }}
                           id={getOptionId(option)}
                           interactive={false}
                           role="option"
                           ariaSelected={value && value.id === option.id}
                           focused={index === focusedIndex}
                           key={option.id}
                           onClick={(e) => handleOptionClick(e, option)}
                           onMouseEnter={() => {
                              setFocusedIndex(index);
                           }}
                        >
                           <Typography whiteSpace="nowrap">
                              <HighlightText text={option.value} searchValue={searchValue} />
                           </Typography>
                        </ListItem>
                     ))}
                  </List>
               </div>
            }
            open={menuOpen}
            maxHeight
            width="fill"
            closeOnOutsideClick={false}
         >
            <div role="combobox" aria-haspopup="listbox" aria-owns={menuId} aria-controls={menuId} aria-expanded={menuOpen} aria-activedescendant={activeOptionId} tabIndex={-1} onMouseDown={handleClick} className="group relative w-full cursor-text">
               {label && (
                  <label
                     data-testid="autocomplete-label"
                     htmlFor={id}
                     style={{
                        maxWidth:
                           value || menuOpen || focused
                              ? 'calc(100% -  2 * var(--component-input-combobox-container-spacing-padding-x))'
                              : 'calc(100% - var(--component-data-display-icon-container-sizing-width-body) - var(--component-input-combobox-container-spacing-gap) - 2 * var(--component-input-combobox-container-spacing-padding-x))'
                     }}
                     className={`absolute left-0 inset-y-0 flex items-center transition-all cursor-pointer
                           ${!error && !disabled && (value || focused) ? 'text-component-input-combobox-label-color-text-neutral-enabled-as-label' : ''}
                           ${!error && !disabled ? 'group-focus-within:text-component-input-combobox-label-color-text-neutral-focused' : ''}
                           ${
                              // istanbul ignore next
                              !error && !disabled && focused ? 'text-component-input-combobox-label-color-text-neutral-focused' : ''
                           }
                           ${!error && !disabled && !value && !menuOpen && !focused ? 'text-component-input-combobox-label-color-text-neutral-enabled-as-placeholder' : ''}
                           ${disabled ? 'text-component-input-combobox-label-color-text-neutral-disabled' : ''}
                           ${error && !disabled ? 'text-component-input-combobox-label-color-text-error-enabled group-hover:!text-component-input-combobox-label-color-text-error-hover' : ''}
                           ${
                              value || menuOpen || focused
                                 ? 'bg-component-input-combobox-container-color-background px-component-input-combobox-label-spacing-padding-x h-[var(--component-input-combobox-container-border-width-enabled)] group-focus-within:h-[var(--component-input-combobox-container-border-width-focused)]'
                                 : 'h-full'
                           }
                           ${
                              // istanbul ignore next
                              focused ? 'h-[var(--component-input-combobox-container-border-width-focused)]' : ''
                           }
                           ${
                              !value && leadingIcon && !focused
                                 ? 'phone:ml-[calc(var(--component-input-combobox-container-spacing-padding-x)+var(--component-input-combobox-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-mobile-body))] tab-port:ml-[calc(var(--component-input-combobox-container-spacing-padding-x)+var(--component-input-combobox-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] tab-land:ml-[calc(var(--component-input-combobox-container-spacing-padding-x)+var(--component-input-combobox-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] ml-[calc(var(--component-input-combobox-container-spacing-padding-x)+var(--component-input-combobox-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-desktop-body))]'
                                 : 'ml-component-input-combobox-container-spacing-padding-x'
                           }
                      `.replace(/\s+/g, ' ')}
                  >
                     <Typography variant={value || focused ? 'caption' : 'body'} color="inherit" maxLines="1">
                        {label}
                        {required && '*'}
                     </Typography>
                  </label>
               )}

               <div
                  data-testid="autocomplete-container"
                  className={`h-component-input-combobox-container-sizing-height px-component-input-combobox-container-spacing-padding-x py-component-input-combobox-container-spacing-padding-y gap-component-input-combobox-container-spacing-gap bg-component-input-combobox-container-color-background rounded-component-input-combobox-container-border-radius border-component-input-combobox-container-border-width-enabled focus-within:!border-component-input-combobox-container-border-width-focused box-border flex items-start justify-between transition-all
                           ${
                              error
                                 ? '!border-component-input-combobox-container-color-border-error-enabled hover:!border-component-input-combobox-container-color-border-error-hover focus-within:!border-component-input-combobox-container-color-border-error-enabled'
                                 : '!border-component-input-combobox-container-color-border-neutral-enabled hover:!border-component-input-combobox-container-color-border-neutral-hover focus-within:!border-component-input-combobox-container-color-border-neutral-focused'
                           }
                           ${menuOpen ? '!border-component-input-combobox-container-border-width-focused' : ''}
                           ${
                              // istanbul ignore next
                              menuOpen && error ? '!border-component-input-combobox-container-color-border-error-enabled' : ''
                           }
                           ${menuOpen && !error ? '!border-component-input-combobox-container-color-border-neutral-enabled' : ''}
                  `.replace(/\s+/g, ' ')}
               >
                  {leadingIcon && (
                     <div
                        data-testid="autocomplete-leading-icon"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                           disabled ? 'text-component-input-combobox-icon-start-color-text-on-surface-disabled' : 'text-component-input-combobox-icon-start-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon={leadingIcon} type={iconType} />
                     </div>
                  )}
                  <div className="flex h-full flex-1 items-center">
                     <input
                        id={id}
                        data-testid="autocomplete-input"
                        autoComplete="off"
                        className="text-component-input-combobox-input-color-text-on-surface-enabled disabled:text-component-input-combobox-input-color-text-on-surface-disabled typography-component-typography-body-regular-md w-full bg-transparent outline-none"
                        disabled={disabled}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyPress}
                        onChange={handleInputChange}
                        onFocus={handleFocus}
                        name={name}
                        ref={inputRef}
                        required={required}
                        readOnly={readOnly}
                        value={inputValue}
                        aria-invalid={error}
                        aria-errormessage={errorMessageId}
                     />
                  </div>
                  {clearable && value && (
                     <div
                        data-testid="autocomplete-clear"
                        onClick={handleClear}
                        role="presentation"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center cursor-pointer ${
                           disabled ? 'text-component-input-search-icon-end-color-text-on-surface-disabled' : 'text-component-input-search-icon-end-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon="times-circle" />
                     </div>
                  )}
                  {trailingIcon && (
                     <div
                        data-testid="autocomplete-trailing-icon"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                           disabled ? 'text-component-input-combobox-icon-end-color-text-on-surface-disabled' : 'text-component-input-combobox-icon-end-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon={trailingIcon} />
                     </div>
                  )}
                  <div
                     data-testid="autocomplete-chevron"
                     className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center transition-all ${
                        disabled ? 'text-component-input-combobox-icon-end-color-text-on-surface-disabled' : 'text-component-input-combobox-icon-end-color-text-on-surface-enabled'
                     } ${menuOpen ? 'rotate-180' : ''}`.replace(/\s+/g, ' ')}
                  >
                     <Icon icon="chevron-down" />
                  </div>
               </div>
            </div>
         </Menu>
         {supportText && (
            <div
               data-testid="autocomplete-support-text"
               className={`pl-component-input-combobox-supporting-text-spacing-padding-left gap-component-input-combobox-supporting-text-spacing-gap flex items-center
                        ${!disabled ? 'text-component-input-combobox-supporting-text-color-text-neutral-enabled' : ''}
                        ${disabled ? 'text-component-input-combobox-supporting-text-color-text-neutral-disabled' : ''}
                        `.replace(/\s+/g, ' ')}
            >
               {supportText && (
                  <Typography variant="caption" color="inherit">
                     {supportText}
                  </Typography>
               )}
            </div>
         )}
         {error && errorMessage && (
            <div data-testid="autocomplete-error-message" className="pl-component-input-combobox-supporting-text-spacing-padding-left gap-component-input-combobox-supporting-text-spacing-gap text-component-input-combobox-supporting-text-color-text-error flex items-center">
               <Icon icon="exclamation-circle" size="caption" />
               <Typography variant="caption" color="inherit" id={errorMessageId}>
                  {errorMessage}
               </Typography>
            </div>
         )}
      </div>
   );
};
